import React, { useEffect, useState } from 'react';
import { Box } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import analytics from '../../../../modules/analytics';
import {
  Button,
  Stack,
  Separator,
  PortalOverlay,
  PortalOverlayModal,
  PortalOverlayHeader,
  PortalOverlayHeaderCloseBtn,
} from '../../../ui';
import type { PublicProvider } from '../../../../api/Providers';
import Title from './Title';
import Message from './Message';
import MessageDetails from './MessageDetails';
import CancelledAppointmentDetails from './CancelledAppointmentDetails';
import { ProAppointmentDetails } from '../../../../api/Providers/Appointments';

type ModalProps = {
  visible: boolean;
  appointment: ProAppointmentDetails;
  provider: PublicProvider;
  hideSearchRanking?: boolean;
  onCloseModal: () => void;
};

const Modal:
React.FC<ModalProps> = ({
  visible,
  appointment,
  provider,
  hideSearchRanking,
  onCloseModal,
}) => {
  const [modalViewed, setModalViewed] = useState<boolean>(false);

  useEffect(() => {
    if (modalViewed || !appointment || !provider || !visible) return;
    analytics.track(
      'Appointment Cancellation Search Rank Impact Modal Viewed',
      {
        provider_id: appointment.provider,
        client_id: appointment.client?.id,
        appointment_id: appointment.id,
      },
    );
    setModalViewed(true);
  }, [
    modalViewed,
    appointment,
    provider,
    visible,
  ]);

  const handleClose = () => {
    if (!appointment || !provider || !visible) return;
    analytics.track(
      'Appointment Cancellation Search Rank Impact Modal Closed',
      {
        provider_id: appointment.provider,
        client_id: appointment.client?.id,
        appointment_id: appointment.id,
      },
    );
    onCloseModal();
  };

  if (!appointment || !provider || !visible) return null;

  return (
    <PortalOverlay name="appointment_cancel_search_rank_impact_modal">
      <PortalOverlayModal
        position="growDialog"
        borderRadius={4}
        boxProps={{
          style: {
            // @ts-ignore
            background: `linear-gradient(to bottom, ${colors.lightChill} 30%, ${colors.whiteWash} 0%)`,
          },
        }}
      >
        <PortalOverlayHeader
          alignItems="center"
          hideBottomBorder
          renderRightAction={() => (
            <PortalOverlayHeaderCloseBtn
              testID="appointment_cancel_search_rank_impact_close_button"
              onCloseRequest={handleClose}
              color={colors.metal}
              size={16}
            />
          )}
        />
        <Stack
          space={16}
          overflow="hidden"
        >
          <Stack
            paddingX={16}
            space={8}
          >
            <Title
              appointment={appointment}
            />
            <CancelledAppointmentDetails
              appointment={appointment}
            />
            {!hideSearchRanking && (
              <Box
                paddingTop={12}
              >
                <Message
                  impact="high"
                />
              </Box>
            )}
          </Stack>
          {!hideSearchRanking && (
            <Separator
              paddingY={0}
              color={colors.lightGrey}
              appearance="extrathin"
            />
          )}
          <Box
            paddingY={hideSearchRanking ? 8 : 0}
            paddingX={16}
            paddingBottom={hideSearchRanking ? 24 : 0}
          >
            <MessageDetails
              provider={provider}
              appointment={appointment}
            />
          </Box>
          {!hideSearchRanking && (
            <Separator
              paddingY={0}
              color={colors.lightGrey}
              appearance="extrathin"
            />
          )}
          {!hideSearchRanking && (
            <Box
              paddingX={16}
              paddingBottom={16}
            >
              <Button
                text="View Search Ranking"
                size="medium-wide"
                appearance="primary"
                link="provider.search_rank"
                linkParams={{
                  viewApptId: appointment.id,
                  providerId: appointment.provider,
                }}
                clickTracking={{
                  name: 'Appointment Cancellation Search Rank Impact Modal View Search Ranking Clicked',
                  params: {
                    provider_id: appointment.provider,
                    client_id: appointment.client?.id,
                    appointment_id: appointment.id,
                  },
                }}
                style={{
                  textTransform: 'none',
                  fontWeight: '400',
                }}
                testID="appointment_cancel_search_rank_impact_view_search_ranking_button"
              />
            </Box>
          )}
        </Stack>
      </PortalOverlayModal>
    </PortalOverlay>
  );
};

export default Modal;
